import React, { useMemo, useState } from "react";
import {
  AttributeKindEnum,
  InvResourceClassEnum,
  InvResourceId,
  InvResourceCategoryId,
} from "@brewbase/db/types";
import { defaultMetadataForClass } from "@brewbase/lib";
import { trpc } from "@/hooks/trpc";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CreateableCombobox, {
  type ComboboxItem as CreateableItem,
} from "@/components/ui/createable-combobox";
import { Combobox } from "@/components/ui/combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";

interface InventoryFieldsValue {
  kind?: AttributeKindEnum;
  inv_item_resource_id?: InvResourceId | null;
  inv_item_resource_category_id?: InvResourceCategoryId | null;
}

interface InventoryFieldsEditorProps {
  value: InventoryFieldsValue;
  onChange: (value: InventoryFieldsValue) => void;
}

export function InventoryFieldsEditor({
  value,
  onChange,
}: InventoryFieldsEditorProps) {
  // Outer switch already gates this to INVENTORY_ITEM_REFERENCE; treat as such
  // unless an explicit kind is supplied.
  const isItemReference =
    value.kind === undefined ||
    value.kind === AttributeKindEnum.INVENTORY_ITEM_REFERENCE;

  // Fetch inventory data
  const { data: resourcesData = [] } = trpc.inventory.resource.list.useQuery({
    include_produced: false,
  });
  const { data: resourceCategoriesData = [] } =
    trpc.inventory.resource.listCategories.useQuery();

  const resourceOptions = useMemo(
    () => resourcesData.map((r) => ({ value: r.id.toString(), label: r.name })),
    [resourcesData],
  );

  const resourceCategoryOptions = useMemo(
    () =>
      resourceCategoriesData.map((rc) => ({
        value: rc.id.toString(),
        label: rc.name,
      })),
    [resourceCategoriesData],
  );

  function ResourceQuickCreateForm({
    input,
    onSubmit,
    onCancel,
  }: {
    input: string;
    onSubmit: (value: string, item: CreateableItem) => void;
    onCancel: () => void;
  }) {
    const utils = trpc.useUtils();
    const createResource = trpc.inventory.resource.create.useMutation({
      onSuccess: async () => {
        await utils.inventory.resource.list.invalidate();
      },
    });
    const [name, setName] = useState<string>(input || "");
    const [categoryId, setCategoryId] = useState<number | "">(
      resourceCategoriesData[0]?.id ?? "",
    );
    const canCreate = !!name && name.trim().length > 0;

    return (
      <div className="space-y-2 bg-white">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-sm">Resource Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-sm">Category (optional)</label>
            <Combobox
              options={resourceCategoriesData.map((c) => ({
                value: String(c.id),
                label: c.name,
              }))}
              value={categoryId ? String(categoryId) : undefined}
              onValueChange={(v?: string) => setCategoryId(v ? Number(v) : "")}
              placeholder="Select category"
              triggerClassName="w-full h-9 text-sm"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1 sticky bottom-2 bg-background">
          <Button
            type="button"
            size="sm"
            disabled={!canCreate || createResource.isPending}
            onClick={async () => {
              const created = await createResource.mutateAsync({
                name,
                formula: "",
                molecular_weight_g_mol: null,
                ...defaultMetadataForClass(InvResourceClassEnum.INGREDIENT),
                category_id: (categoryId || null) as any,
              } as any);
              if (created?.id) {
                onSubmit(String(created.id), {
                  value: String(created.id),
                  label: created.name as any,
                });
              }
            }}
          >
            <Plus className="h-3 w-3 mr-1" /> Create Resource
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Helper functions to get display values
  const getResourceDisplayValue = (id: string | number | null | undefined) => {
    if (!id || id === "__none__") return "No specific resource";
    const resource = resourceOptions.find((r) => r.value === id.toString());
    return resource ? resource.label : id.toString();
  };

  const getResourceCategoryDisplayValue = (
    id: string | number | null | undefined,
  ) => {
    if (!id || id === "__none__") return "No category";
    const category = resourceCategoryOptions.find(
      (c) => c.value === id.toString(),
    );
    return category ? category.label : id.toString();
  };

  if (isItemReference) {
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground">
          Inventory Item Settings
        </h4>

        {/* Specific Resource */}
        <div className="space-y-1">
          <Label className="text-xs font-medium">Specific Resource</Label>

          <CreateableCombobox
            items={[
              { value: "__none__", label: "No specific resource" },
              ...resourcesData
                .map((r) => ({ value: String(r.id), label: r.name }))
                .toSorted((a, b) => a.label.localeCompare(b.label)),
            ]}
            value={
              value.inv_item_resource_id === null ||
              value.inv_item_resource_id === undefined
                ? "__none__"
                : String(value.inv_item_resource_id)
            }
            onChange={(v?: string) =>
              onChange({
                ...value,
                inv_item_resource_id:
                  !v || v === "__none__"
                    ? null
                    : (parseInt(v) as InvResourceId),
              })
            }
            onCreateItem={() => {}}
            CreateItemFormComponent={ResourceQuickCreateForm}
            itemName="resource"
            placeholder="Search or create resource..."
            buttonClassName="w-full"
            popoverWidth="w-[420px]"
          />
        </div>

        {/* Resource Category */}
        <div className="space-y-1">
          <Label className="text-xs font-medium">Resource Category</Label>

          <Select
            value={
              value.inv_item_resource_category_id !== null &&
              value.inv_item_resource_category_id !== undefined
                ? String(value.inv_item_resource_category_id)
                : "__none__"
            }
            onValueChange={(val) =>
              onChange({
                ...value,
                inv_item_resource_category_id:
                  val === "__none__"
                    ? null
                    : (parseInt(val) as InvResourceCategoryId),
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select resource category..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No category</SelectItem>
              {resourceCategoryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <p className="text-xs text-muted-foreground">
          Either a specific resource or a category is required.
        </p>
      </div>
    );
  }

  return null;
}
